// The AI Actor Builder's lists, live's words and order (create-avatar.tsx; SA4, #221): every
// list the Builder shows. builder-options.test.ts pins them to live's.
import type {
  AiActorAge,
  AiActorBackground,
  AiActorBody,
  AiActorClothingStyle,
  AiActorEthnicity,
  AiActorEyes,
  AiActorFace,
  AiActorFraming,
  AiActorHairColor,
  AiActorHairLength,
  AiActorLighting,
  AiActorShotAngle,
} from "../../../api/generated/model";

export type Option<T extends string> = { value: T; label: string };

/** Live's six ages, the slider's stops (`CREATE_AVATAR_AGE_LABELS`). */
export const AGES: Option<AiActorAge>[] = [
  { value: "child", label: "Child" },
  { value: "teen", label: "Teen" },
  { value: "young_adult", label: "Young" },
  { value: "middle_aged", label: "Middle aged" },
  { value: "senior", label: "Senior" },
  { value: "elderly", label: "Elderly" },
];

export const ETHNICITIES: Option<AiActorEthnicity>[] = [
  { value: "african_american", label: "African American" },
  { value: "asian_american", label: "Asian American" },
  { value: "caucasian_american", label: "Caucasian American" },
  { value: "east_asian", label: "East Asian" },
  { value: "european_caucasian", label: "European Caucasian" },
  { value: "indigenous_american", label: "Indigenous American" },
  { value: "middle_eastern_north_african", label: "Middle Eastern / North African" },
  { value: "mixed_multiracial", label: "Mixed / Multiracial" },
  { value: "south_asian", label: "South Asian" },
  { value: "southeast_asian", label: "Southeast Asian" },
  { value: "sub_saharan_african", label: "Sub-Saharan African" },
  { value: "other", label: "Other" },
];

export const BODIES: Option<AiActorBody>[] = [
  { value: "petite", label: "Petite" },
  { value: "slim", label: "Slim" },
  { value: "average", label: "Average" },
  { value: "athletic", label: "Athletic" },
  { value: "curvy", label: "Curvy" },
  { value: "plus_size", label: "Plus size" },
  { value: "muscular", label: "Muscular" },
  { value: "tall", label: "Tall" },
];

/** Live's tiles in its order; each sends its own shape (live swaps Heart and Square, SA4). */
export const FACES: Option<AiActorFace>[] = [
  { value: "oval", label: "Oval" },
  { value: "round", label: "Round" },
  { value: "heart", label: "Heart" },
  { value: "square", label: "Square" },
];

export const EYES: Option<Exclude<AiActorEyes, "custom">>[] = [
  { value: "brown", label: "Brown" },
  { value: "blue", label: "Blue" },
  { value: "green", label: "Green" },
  { value: "hazel", label: "Hazel" },
  { value: "gray", label: "Gray" },
];

export const HAIR_LENGTHS: Option<AiActorHairLength>[] = [
  { value: "short", label: "Short" },
  { value: "long", label: "Long" },
];

export const HAIR_COLORS: Option<Exclude<AiActorHairColor, "custom">>[] = [
  { value: "black", label: "Black" },
  { value: "brown", label: "Brown" },
  { value: "blonde", label: "Blonde" },
  { value: "red", label: "Red" },
];

export const CLOTHING: Option<AiActorClothingStyle>[] = [
  { value: "casual", label: "Casual" },
  { value: "formal", label: "Formal" },
  { value: "business", label: "Business" },
  { value: "athletic", label: "Athletic" },
  { value: "streetwear", label: "Streetwear" },
  { value: "other", label: "Other" },
];

export const FRAMINGS: Option<AiActorFraming>[] = [
  { value: "headshot", label: "Headshot" },
  { value: "half_body", label: "Half Body" },
  { value: "full_body", label: "Full Body" },
];

export const ANGLES: Option<AiActorShotAngle>[] = [
  { value: "front", label: "Front" },
  { value: "side", label: "Side" },
  { value: "back", label: "Back" },
];

export const SEATS: Option<"yes" | "no">[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];

export const LIGHTINGS: Option<AiActorLighting>[] = [
  { value: "natural", label: "Natural" },
  { value: "studio", label: "Studio" },
  { value: "soft", label: "Soft" },
  { value: "hard", label: "Hard" },
  { value: "dramatic", label: "Dramatic" },
  { value: "backlighting", label: "Backlighting" },
  { value: "golden_hour", label: "Golden hour" },
  { value: "low_light", label: "Low light" },
  { value: "other", label: "Other" },
];

export const BACKGROUNDS: Option<AiActorBackground>[] = [
  { value: "studio_white_backdrop", label: "Studio white backdrop" },
  { value: "office_workspace", label: "Office workspace" },
  { value: "home_kitchen", label: "Home kitchen" },
  { value: "home_living_room", label: "Home living room" },
  { value: "outdoor_park", label: "Outdoor park" },
  { value: "city_street", label: "City street" },
  { value: "beach", label: "Beach" },
  { value: "cafe_restaurant", label: "Cafe restaurant" },
  { value: "gym_fitness", label: "Gym Fitness" },
  { value: "shopping_mall", label: "Shopping mall" },
  { value: "car_interior", label: "Car interior" },
  { value: "rooftop_urban", label: "Rooftop urban" },
  { value: "forest_nature", label: "Forest nature" },
  { value: "warehouse_industrial", label: "Warehouse industrial" },
  { value: "hotel_lobby", label: "Hotel lobby" },
  { value: "airplane_interior", label: "Airplane interior" },
  { value: "hospital_medical", label: "Hospital medical" },
  { value: "school_classroom", label: "School classroom" },
  { value: "luxury_bedroom", label: "Luxury bedroom" },
  { value: "corporate_boardroom", label: "Corporate boardroom" },
];
